/* #963 — GA4 `add_to_wishlist` dataLayer push for the Wishlist King / Appmate app.
 *
 * GTM config (tag/trigger/variable) is Dot Collective's separate job; this file owns
 * the dataLayer push only.
 *
 * Delivery: Wishlist King dispatches a DOCUMENTED, supported `document` event
 * `wk:wishlist:add:success` (Swish docs → Customizations → Wishlist Notifications) when
 * the server CONFIRMS an add. Its `event.detail.data` carries `{ wishlist, product,
 * variant }` — the exact added variant included — so we build our spec-compliant GA4
 * event straight from it and push it through GTM's own (untouched) dataLayer.push.
 *
 * Why this event, not the button click: the click is optimistic — Wishlist King toggles
 * `wk-selected` before its API confirms, and the same control REMOVES on a second click.
 * `wk:wishlist:add:success` is the honest signal: it fires once, only on a confirmed add,
 * on every surface (PDP, card, drawer, cart), and it hands us the variant — so there is
 * no click-arming, no dataLayer polling, and no chance of attributing an add to the wrong
 * product.
 *
 * Why not wrap dataLayer.push to suppress the app's own (non-compliant) push: wrapping
 * stops GTM processing events (its debug push re-enters push; the guard needed to avoid a
 * stack overflow routes those internal calls to the native array, so GTM never fires the
 * tag). So the app's event is left in place and GTM fires the tag only for OURS via a
 * trigger condition (item_id starts with "shopify_") — DC's tag config.
 */
(function () {
  'use strict';
  if (window.__jagWishlistDataLayer) return;
  window.__jagWishlistDataLayer = true;

  // The confirmed-success event Wishlist King dispatches on `document`. detail = the
  // published message: { name, data: { wishlist, product, variant } }.
  var EVENT = 'wk:wishlist:add:success';
  // item_name parity with the Google & YouTube app (brief §3a): "<product title> - <variant
  // title>" (hyphen), e.g. "Carrie Slouch Jean - 23/5". Title alone for single-variant /
  // Default-Title products.
  var SEPARATOR = ' - ';

  // Merchant-Center country segment of the offer id. Driven by the market the customer is
  // browsing — the theme renders localization.market.handle onto <body data-market>, so
  // this is correct on both the .nz domain and the /en-nz/ market path.
  function market() {
    var m = document.body && document.body.getAttribute('data-market');
    return m === 'new-zealand' ? 'NZ' : 'AU';
  }
  // Active presentment currency (AUD / NZD) — matches the presentment variant.price below.
  function currency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || undefined;
  }

  // Build the GA4 add_to_wishlist ecommerce object from the app's confirmed event.
  function specEvent(product, variant) {
    if (!product || product.id == null) return null;
    // Resolve the added variant; fall back to the sole variant for single-variant products.
    if ((!variant || variant.id == null) && product.variants && product.variants.length === 1) {
      variant = product.variants[0];
    }
    if (!variant || variant.id == null) return null;

    var isDefault = variant.title === 'Default Title';
    var single = !product.variants || product.variants.length <= 1;
    // variant.price is presentment MINOR units (cents, per the app's Storefront-API
    // normaliser). GA4 wants a decimal amount, so /100. Never send null.
    var price = typeof variant.price === 'number' && isFinite(variant.price) ? variant.price / 100 : null;

    var item = {
      item_id: 'shopify_' + market() + '_' + product.id + '_' + variant.id,
      item_name: single || isDefault ? product.title : product.title + SEPARATOR + variant.title,
      quantity: 1
    };
    if (price !== null) item.price = price;
    if (product.vendor) item.item_brand = product.vendor;
    if (product.type) item.item_category = product.type;
    if (!isDefault && variant.title) item.item_variant = variant.title;

    var ecommerce = { currency: currency(), items: [item] };
    if (price !== null) ecommerce.value = price;
    return { event: 'add_to_wishlist', ecommerce: ecommerce };
  }

  function onAdd(e) {
    var data = e && e.detail && e.detail.data;
    if (!data) return;
    var payload = specEvent(data.product, data.variant);
    if (!payload) return;
    // Push through the array's OWN push (GTM has wrapped it → GTM processes + fires).
    var dl = (window.dataLayer = window.dataLayer || []);
    dl.push({ ecommerce: null }); // clear any prior ecommerce object so ours doesn't merge
    dl.push(payload); // GTM processes it → GA4 tag fires (trigger: item_id starts with shopify_)
  }

  // Attach immediately; the listener is passive and catches the event whenever the app
  // dispatches it (no race with the app loading, no polling).
  document.addEventListener(EVENT, onAdd);
})();
